import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Waves, Play, Shuffle, Loader2, Drum } from "lucide-react";

let _prevAudio = null;
async function playPreview(id) {
  try {
    const r = await api.get(`/library/asset/${id}/preview`, { responseType: "blob" });
    if (_prevAudio) { _prevAudio.pause(); }
    _prevAudio = new Audio(URL.createObjectURL(r.data));
    await _prevAudio.play();
    return true;
  } catch (e) {
    toast.message("This sound lives on your machine", { description: "Preview it in your DAW — only imported free-pack sounds play here." });
    return false;
  }
}

const Row = ({ label, note, name, assetId, canPreview, onSwap, swapping, testid }) => {
  const [playing, setPlaying] = useState(false);
  const prev = async () => { setPlaying(true); await playPreview(assetId); setTimeout(() => setPlaying(false), 900); };
  return (
    <div className="flex items-center gap-2 py-1.5" data-testid={testid}>
      <span className="text-[10px] text-slate-500 w-16 shrink-0 font-mono-a">{label}{note ? ` · ${note}` : ""}</span>
      <span className="text-sm text-slate-200 truncate flex-1">{name || <span className="text-slate-600">— no match —</span>}</span>
      <button data-testid={`${testid}-preview`} onClick={prev} disabled={!canPreview} title={canPreview ? "Preview" : "On your machine — preview in your DAW"} className="p-1.5 rounded-md text-slate-400 hover:text-cyan hover:bg-white/5 disabled:opacity-25">{playing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}</button>
      <button data-testid={`${testid}-swap`} onClick={onSwap} disabled={swapping} title="Try another from your library" className="p-1.5 rounded-md text-slate-400 hover:text-gold hover:bg-white/5">{swapping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shuffle className="w-3.5 h-3.5" />}</button>
    </div>
  );
};

export const MatchedSounds = ({ pid }) => {
  const [data, setData] = useState(null);
  const [swapping, setSwapping] = useState(null);

  const load = useCallback(() => api.get(`/projects/${pid}/matches`).then((r) => setData(r.data)).catch(() => setData({ matches: {}, preview_available: {} })), [pid]);
  useEffect(() => { load(); }, [load]);

  const swap = async (track, lane) => {
    setSwapping(track + (lane || ""));
    try { await api.post(`/projects/${pid}/matches/swap`, { track, lane }); await load(); toast.success("Swapped for another sound"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No other match in your library"); }
    finally { setSwapping(null); }
  };

  const matches = data?.matches || {};
  const pv = data?.preview_available || {};
  const entries = Object.entries(matches);
  if (!entries.length) return null;

  return (
    <div className="mt-4 card-surface rounded-xl p-4" style={{ background: "var(--daw-panel-2)" }} data-testid="matched-sounds">
      <div className="flex items-center gap-2 mb-2">
        <Waves className="w-4 h-4 text-gold" />
        <p className="text-slate-100 font-medium text-sm">Your matched sounds</p>
        <span className="text-[10px] text-slate-500 ml-1">from your library — loaded onto tracks in REAPER</span>
      </div>
      <div className="divide-y divide-white/5">
        {entries.map(([track, m]) => (
          <div key={track} className="py-2">
            <p className="text-[11px] text-gold flex items-center gap-1.5 mb-0.5"><Drum className="w-3 h-3" /> {track}</p>
            {m.type === "drum_kit"
              ? Object.entries(m.parts || {}).map(([lane, p]) => (
                  <Row key={lane} testid={`match-${track}-${lane}`} label={lane} note={p.note} name={p.name} assetId={p.id} canPreview={!!pv[p.id]} swapping={swapping === track + lane} onSwap={() => swap(track, lane)} />
                ))
              : <Row testid={`match-${track}`} label={m.lane || m.type} note={m.note} name={m.file?.name} assetId={m.file?.id} canPreview={!!pv[m.file?.id]} swapping={swapping === track} onSwap={() => swap(track)} />}
          </div>
        ))}
      </div>
    </div>
  );
};
